import { createSlice } from '@reduxjs/toolkit';
import { sampleBoatList } from '../../TestData';
import { BoatDetailsState, BoatGroup } from './BoatDetails';
import { performanceTracker } from '../../services/PerformanceTracker';
import { analyticsService } from '../../services/AnalyticsService';

const initialState = {
  baseBoatId: '',
  boatDetailsList: sampleBoatList,
  currentGroupId: '',
  boatGroupList: [],
} as BoatDetailsState;

function findCurrentBoatGroup(state: BoatDetailsState): BoatGroup | undefined {
  return state.boatGroupList.find((g) => g.id === state.currentGroupId);
}

// function findCurrentBoatGroup

export const boatListSlice = createSlice({
  name: 'boatList',
  initialState,
  reducers: {
    add: (state, action) => {
      const startTime = performance.now();
      console.log('boatListSlice.reducer.add action', action);
      const boatgroup = findCurrentBoatGroup(state);
      if (boatgroup) {
        boatgroup.boatDetails.push(action.payload);

        // Track performance for large boat lists
        const endTime = performance.now();
        const duration = endTime - startTime;

        if (boatgroup.boatDetails.length > 20 || duration > 5) {
          performanceTracker.trackCustomTiming(
            'boat_list_add',
            startTime,
            endTime,
            {
              boat_count: boatgroup.boatDetails.length,
              handicap_system: boatgroup.handicapSystem || 'unknown',
              operation: 'add',
            },
          );
        }
      } else {
        console.error('Unable to find group to add boat details to', state.currentGroupId);
      }
    },
    remove: (state, action) => {
      console.log('boatListSlice.reducer.remove action', action);
      const boatgroup = findCurrentBoatGroup(state);
      if (boatgroup) {
        boatgroup.boatDetails = boatgroup.boatDetails.filter(function(item) {
          return item.id !== action.payload.id;
        });
      } else {
        console.error('Unable to find group to remove boat details from', state.currentGroupId);
      }
    },
    update: (state, action) => {
      console.log('boatListSlice.reducer.update action', action);
      const boatgroup = findCurrentBoatGroup(state);
      if (boatgroup) {
        // TODO look at just replacign/updatign it in place
        // Remove the existing one
        boatgroup.boatDetails = boatgroup.boatDetails.filter(function(item) {
          return item.id !== action.payload.id;
        });
        // Add the new version
        boatgroup.boatDetails.push(action.payload);
      } else {
        console.error('Unable to find group to update boat details to', state.currentGroupId);
      }
    },
    selectBaseBoat: (state, action) => {
      console.log('boatListSlice.reducer.baseBoat action', action);
      const boatgroup = state.boatGroupList.find((g) => g.id === state.currentGroupId);
      if (boatgroup) {
        boatgroup.baseBoatId = action.payload;
      } else {
        console.error('Unable to find group to select base boat', state.currentGroupId);
      }
    },
    replaceAll: (state, action) => {
      const startTime = performance.now();
      console.log('boatListSlice.reducer.replaceAll action', action);
      state.boatGroupList = action.payload.boatGroupList;
      state.currentGroupId = action.payload.currentGroupId;

      // Track performance for bulk operations
      const endTime = performance.now();
      const duration = endTime - startTime;
      const totalBoats = state.boatGroupList.reduce((sum, group) => sum + group.boatDetails.length, 0);

      performanceTracker.trackCustomTiming(
        'boat_list_replace_all',
        startTime,
        endTime,
        {
          total_boats: totalBoats,
          group_count: state.boatGroupList.length,
          operation: 'replace_all',
        },
      );

      // Log bulk data operation
      analyticsService.logEvent('boat_data_bulk_operation', {
        operation_type: 'replace_all',
        total_boats: totalBoats,
        group_count: state.boatGroupList.length,
        operation_duration_ms: duration,
      });
    },
    addGroup: (state, action) => {
      console.log('boatListSlice.reducer.addGroup action', action);
      state.boatGroupList.push(action.payload);
      state.currentGroupId = action.payload.id;
    },
    removeGroup: (state, action) => {
      console.log('boatListSlice.reducer.removeGroup action', action);
      state.boatGroupList = state.boatGroupList.filter(function(item) {
        return item.id !== action.payload.id;
      });
    },
    updateGroup: (state, action) => {
      console.log('boatListSlice.reducer.updateGroup action', action);
      // TODO look at just replacign/updatign it in place
      // Remove the existing one
      state.boatGroupList = state.boatGroupList.filter(function(item) {
        return item.id !== action.payload.id;
      });
      // Add the new version
      state.boatGroupList.push(action.payload);
    },
    selectCurrentGroup: (state, action) => {
      console.log('boatListSlice.reducer.selectCurrentGroup action', action);
      state.currentGroupId = action.payload;
    },
  },
});

// Action creators are generated for each case reducer function
export const {
  add, remove, update, selectBaseBoat, replaceAll,
  addGroup, removeGroup, updateGroup, selectCurrentGroup,
} = boatListSlice.actions;

export default boatListSlice.reducer;
